import { createEvent } from 'ics';
import { tzlib_get_ical_block as getIcsVTimezone } from 'timezones-ical-library';
import { dayjs } from '@attendu/utils';

// Human-readable venue string shared by the .ics file and the Google Calendar
// mirror, so both show the same place. Online-only events have no venue.
export const getEventLocationString = (event) => {
  if (event?.general?.isOnlineOnly || !event?.general?.location?.placeName) {
    return '';
  }

  const { placeName, address, city, zipCode, country } = event.general.location;

  return [placeName, address, city, zipCode, country].filter(Boolean).join(', ');
};

const toLocalDateArray = (date, timezone) => dayjs(date)
  .tz(timezone)
  .format('YYYY-MM-DD-HH-mm')
  .split('-')
  .map((item) => Number(item));

/**
 * Builds the iCalendar payload for an event.
 *
 * Without `method` this is the plain PUBLISH file we have always attached.
 * With `method: 'REQUEST'` plus `uid`, `organizer` and `attendee` it becomes a
 * real invitation that mail clients render with RSVP buttons; when `uid` is the
 * iCalUID of the mirrored Google Calendar event, Gmail pairs both into one.
 */
export default ({
  event,
  title,
  organizer,
  method,
  uid,
  attendee,
  sequence,
}) => {
  const timezone = event.general?.timezone;

  const { value, error } = createEvent({
    productId: 'Envelope',
    title,
    start: toLocalDateArray(event.general?.startDate, timezone),
    startOutputType: 'local',
    startInputType: 'local',
    end: toLocalDateArray(event.general?.endDate, timezone),
    endOutputType: 'local',
    endInputType: 'local',
    description: '',
    location: getEventLocationString(event),
    organizer,
    ...(method ? { method } : {}),
    ...(uid ? { uid } : {}),
    ...(Number.isInteger(sequence) ? { sequence } : {}),
    ...(attendee
      ? {
        status: 'CONFIRMED',
        attendees: [{
          name: attendee.name || attendee.email,
          email: attendee.email,
          rsvp: true,
          partstat: 'NEEDS-ACTION',
          role: 'REQ-PARTICIPANT',
        }],
      }
      : {}),
  });

  if (error) {
    console.error(error);
    return '';
  }

  if (!value) {
    console.error('ics generation failed', event);
  }

  const vTimezone = getIcsVTimezone(timezone);
  const valueWithTimezone = value
    .replace('DTSTART:', `DTSTART;${vTimezone?.[1] || ''}:`)
    .replace('DTEND:', `DTEND;${vTimezone?.[1] || ''}:`)
    .replace('BEGIN:VEVENT', `${vTimezone?.[0] || ''}\r\nBEGIN:VEVENT`);

  return valueWithTimezone;
};
